import { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import BarChartIcon from '@mui/icons-material/BarChart'
import { useSelector, useDispatch } from 'react-redux'
import Beacon4KpiLineChart from './charts/Beacon4KpiLineChart'
import Beacon4PerformanceAdminDialog from './admin/Beacon4PerformanceAdminDialog'
import Beacon4PerformanceAggregationTable from './Beacon4PerformanceAggregationTable'
import {
  selectBeacon4PerformanceDashboardByTech,
  selectBeacon4PerformanceLoading,
} from '../reducers/Beacon4PerformanceKpiDataReducer'
import {
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4VisibleSeriesLeft,
  selectBeacon4VisibleSeriesRight,
  selectBeacon4AdminDialogOpen,
} from '../reducers/Beacon4FiltersReducer'
import { closeBeacon4PerformanceAdmin } from '../actions/Beacon4PerformanceModuleActions'
import { getColorForKey } from './filters/Beacon4PerformanceLegendPanel'

function EmptyState({ title, subtitle }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 400, gap: 1.5, color: 'text.disabled',
    }}>
      <BarChartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 11 }}>{subtitle}</Typography>}
    </Box>
  )
}

// Convert N single-series DTOs into a chart DTO with N overlaid series.
// `seriesOffset` is the index of the first series in the global colour palette,
// so that left- and right-panel colours don't collide.
function toChartDto(dtos, title, visibleSeries, seriesOffset = 0) {
  if (!dtos || dtos.length === 0) return null

  // Union of all date labels across all DTOs (sorted)
  const labelSet = new Set()
  dtos.forEach(d => (d.labels ?? []).forEach(l => labelSet.add(l)))
  const labels = [...labelSet].sort()

  // Build aligned series. Each series keeps its own colour, matching the legend.
  const series = dtos
    .map((d, i) => {
      const seriesKey = d.kpiName
      // Apply legend visibility filter (null = all visible)
      if (visibleSeries !== null && !visibleSeries.includes(seriesKey)) return null
      const map = {}
      ;(d.labels ?? []).forEach((l, j) => { map[l] = (d.data ?? [])[j] })
      return {
        key:           seriesKey,
        label:         d.kpiName,
        unit:          d.unit,
        color:         getColorForKey(seriesKey, seriesOffset + i),
        data:          labels.map(l => (l in map ? map[l] : null)),
        // Admin-controlled ('line' | 'area', KPI Manager > Presentation) —
        // the chart uses this directly to decide fill vs. plain line.
        chartStyle:    d.chartStyle,
        // Fallback signals only, used by the chart when chartStyle is
        // missing (e.g. a KPI created before this field existed and not yet
        // re-saved): short SQL key + "is this panel's own Main KPI".
        sourceKpiKey:  d.kpiKey,
        isPrimary:     i === 0,
      }
    })
    .filter(Boolean)

  return {
    kpiKey:   title,
    kpiName:  title,
    unit:     dtos[0]?.unit ?? '',
    isRate:   dtos[0]?.isRate ?? false,
    slaGreen: null,
    slaRed:   null,
    labels,
    series,
  }
}

// Bounds for the user-resizable charts row (in pixels).
const CHART_MIN_H = 220
const CHART_MAX_H = 640

/**
 * Resize handle placed on the boundary between the charts row and the table
 * panel. Dragging it directly controls the CHARTS' height — dragging up
 * shrinks the charts, dragging down grows them. The table has no manual
 * height of its own (see Beacon4PerformanceAggregationTable's `autoHeight`),
 * so shrinking the charts simply reveals more of the table beneath, which is
 * what "the table grows" looks like from the user's point of view.
 *
 * Kept deliberately plain: a constant, clearly visible grey bar (no hidden
 * state, no hover-triggered animation) rather than a subtle 1px line that's
 * easy to miss, or a busy button-like control with an icon.
 * Double-click resets the charts back to the default viewport-based height.
 */
function ChartResizeHandle({ onResize, onReset }) {
  const draggingRef = useRef(false)
  const lastYRef     = useRef(0)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return
      const delta = e.clientY - lastYRef.current
      lastYRef.current = e.clientY
      // Dragging UP (delta < 0) shrinks the charts; dragging DOWN grows them.
      onResize(h => Math.min(CHART_MAX_H, Math.max(CHART_MIN_H, h + delta)))
    }
    const onMouseUp = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      setDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [onResize])

  const onMouseDown = (e) => {
    draggingRef.current = true
    setDragging(true)
    lastYRef.current = e.clientY
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }

  return (
    <Box
      onMouseDown={onMouseDown}
      onDoubleClick={onReset}
      title="Drag to resize charts · double-click to reset"
      sx={{
        flexShrink: 0,
        height: 6,
        my: 0.75,
        borderRadius: 1,
        cursor: 'row-resize',
        bgcolor: dragging ? 'grey.400' : 'grey.200',
        '&:hover': { bgcolor: 'grey.400' },
      }}
    />
  )
}

export default function Beacon4PerformanceView({ externalRole }) {
  const dispatch  = useDispatch()
  const adminOpen = useSelector(selectBeacon4AdminDialogOpen)
  const canAdmin = externalRole === 'Administrator' || externalRole === 'Manager'

  const dashboardByTech = useSelector(selectBeacon4PerformanceDashboardByTech)
  const loading         = useSelector(selectBeacon4PerformanceLoading)
  const tech            = useSelector(selectBeacon4PerfTech)
  const mainKpi         = useSelector(selectBeacon4PerfMainKpi)
  const visibleLeft     = useSelector(selectBeacon4VisibleSeriesLeft)
  const visibleRight    = useSelector(selectBeacon4VisibleSeriesRight)

  // Chart height as a NUMBER (not "100%") — Chart.js's resize on a
  // percentage-height canvas inside a flex box can shrink the box by 1px
  // per frame, creating a slow-collapse loop. A fixed-px height lets the
  // chart measure once and stop.
  //
  // Default is viewport-based (~42% of window height, clamped). Once the
  // user manually drags the handle below, we stop auto-recomputing this on
  // window resize — a manual adjustment should never be silently undone,
  // same reasoning used elsewhere in this component for user-driven sizing.
  const computeChartH = () => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800
    return Math.max(CHART_MIN_H, Math.min(CHART_MAX_H, Math.round(vh * 0.42)))
  }
  const [chartH, setChartH] = useState(computeChartH)
  const userAdjustedRef = useRef(false)

  useEffect(() => {
    const onWindowResize = () => {
      if (userAdjustedRef.current) return // respect the user's manual size
      setChartH(computeChartH())
    }
    window.addEventListener('resize', onWindowResize)
    return () => window.removeEventListener('resize', onWindowResize)
  }, [])

  const onDragChart = (updater) => {
    userAdjustedRef.current = true
    setChartH(updater)
  }
  const onResetChart = () => {
    userAdjustedRef.current = false
    setChartH(computeChartH())
  }

  // Decide the body content, but DON'T early-return — the admin dialog (and
  // the loading indicator) must stay mounted regardless of whether a KPI is
  // selected or data has loaded yet. Otherwise "Manage KPIs" just dispatches
  // a Redux flag with no dialog in the tree to react to it, and the button
  // silently does nothing until the user has pressed Apply at least once.
  let body

  if (!mainKpi) {
    body = (
      <EmptyState
        title="Select a KPI and press Apply"
        subtitle={`Choose a Tech and KPI in the filter bar to load the ${tech} dashboard`}
      />
    )
  } else {
    const data = dashboardByTech?.[tech]?.[mainKpi]

    if (!data && !loading) {
      body = (
        <EmptyState
          title="Press Apply to load data"
          subtitle={`${tech} · ${mainKpi.toUpperCase()}`}
        />
      )
    } else {
      const leftPanel  = data?.leftPanel  ?? []
      const rightPanel = data?.rightPanel ?? []

      const leftDto  = toChartDto(leftPanel,  'KPI Performance', visibleLeft,  0)
      const rightDto = toChartDto(rightPanel, 'KPI Failures',    visibleRight, leftPanel.length)

      const mainTitle = leftPanel?.[0]?.kpiName ?? mainKpi.toUpperCase()

      body = (
        <>
          {/* Two side-by-side panels — height controlled by the drag handle
              below (or the viewport-based default until the user drags it). */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: rightPanel.length > 0 ? { xs: '1fr', lg: '1fr 1fr' } : '1fr',
            gap: 1.5,
            width: '100%',
            overflow: 'hidden',
            flexShrink: 0,
          }}>
            {/* LEFT: Main KPI + sub-components */}
            <Box data-chart-title={`Performance ${tech} ${mainKpi}`} sx={{ minWidth: 0 }}>
              <Beacon4KpiLineChart
                title={`KPI Performance — ${mainTitle}`}
                seriesDto={leftDto}
                kpiKey={`perf_left_${tech}_${mainKpi}`}
                height={chartH}
              />
            </Box>

            {/* RIGHT: Availability + raw counter failures */}
            {rightPanel.length > 0 && (
              <Box data-chart-title={`Failures ${tech} ${mainKpi}`} sx={{ minWidth: 0 }}>
                <Beacon4KpiLineChart
                  title="KPI Failures"
                  seriesDto={rightDto}
                  kpiKey={`perf_right_${tech}_${mainKpi}`}
                  height={chartH}
                />
              </Box>
            )}
          </Box>

          {/* Drag handle — shrinks/grows the charts above. The table below
              has no manual height of its own, so shrinking the charts simply
              reveals more of it. Double-click resets to the default height. */}
          <ChartResizeHandle onResize={onDragChart} onReset={onResetChart} />

          {/* BOTTOM: Aggregation Mode breakdown table — sizes itself to
              exactly fit the current page size (25/50/100 rows), no manual
              height here. */}
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            <Beacon4PerformanceAggregationTable />
          </Box>
        </>
      )
    }
  }

  return (
    <Box sx={{
      position: 'relative',
      // Parent (App.chartArea) is a flex column with defined height when in
      // Performance mode, so flex: 1 + minHeight: 0 is enough to fill it.
      // The table below has NO manual height (it auto-sizes to its current
      // page size via `autoHeight`), so if chart + table together exceed the
      // available space, this container scrolls instead of clipping content.
      flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column',
      overflowY: 'auto', overflowX: 'hidden',
    }}>
      {/* Loading indicator — always mounted, just conditionally visible */}
      {loading && (
        <Box sx={{
          position: 'absolute', top: 8, right: 8, zIndex: 5,
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, px: 1.5, py: 0.5,
          border: '1px solid', borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}>
          <CircularProgress size={12} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading…</Typography>
        </Box>
      )}

      {/* Admin dialog — ALWAYS mounted (when the role allows it), so
          "Manage KPIs" works immediately, before any KPI is selected or
          Apply has ever been pressed. This must never move back inside a
          branch that can early-return. */}
      {canAdmin && (
        <Beacon4PerformanceAdminDialog open={adminOpen} onClose={() => dispatch(closeBeacon4PerformanceAdmin())} />
      )}

      {body}
    </Box>
  )
}